import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import { getCommunityHub } from "../../../services/communityApi";
import { trackEvent } from "../../../shared/analytics";
import { BOOK_CLUB_PATH, buildAuthRedirectPath } from "../../auth/authRedirect";
import {
  formatBookClubSchedule,
  hasBookClubEnded,
} from "../../community/bookClubSchedule";

interface BookClubSummary {
  title: string;
  description: string;
  cover?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  status: string;
}

interface CommunityHubResponse {
  data?: {
    bookClub?: BookClubSummary | null;
  };
}

const useBookClub = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data } = useQuery<CommunityHubResponse>(
    ["communityHub", timezone],
    () => getCommunityHub(timezone),
    {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
  const club = data?.data?.bookClub;

  return club &&
    ["upcoming", "active"].includes(club.status) &&
    !hasBookClubEnded(club.endsAt)
    ? club
    : null;
};

export const BookClubAnnouncement = () => {
  const club = useBookClub();

  if (!club) {
    return null;
  }

  return (
    <Box
      as="aside"
      aria-label="Book Club announcement"
      className="book-club-announcement"
      h={{ base: "68px", md: "64px" }}
      bg="#000000"
      borderBottomWidth="1px"
      borderColor="transparent"
      color="white"
      position="relative"
      zIndex={2}
    >
      <Container maxW="container.xl" h="100%">
        <Flex align="center" gap={4} h="100%" justify="space-between">
          {club.cover && (
            <Image
              src={club.cover}
              alt="Book Club cover"
              className="book-club-announcement-cover"
              flex="0 0 auto"
              alignSelf="center"
              w={{ base: "44px", md: "60px" }}
              h={{ base: "60px", md: "56px" }}
              objectFit="cover"
              objectPosition="center"
              borderRadius="sm"
              boxShadow="0 0 18px rgba(40, 199, 111, 0.24)"
            />
          )}
          <Text
            flex="1"
            minW={0}
            fontSize={{ base: "sm", md: "md" }}
            fontWeight="semibold"
            noOfLines={{ base: 2, md: 1 }}
          >
            Doomsday is coming. Join our Book Club and get ready.
          </Text>
          <Button
            as={Link}
            to={buildAuthRedirectPath("/login", BOOK_CLUB_PATH)}
            aria-label="Join the Book Club"
            className="book-club-promo-cta book-club-announcement-cta button-shadow"
            flex="0 0 auto"
            size="sm"
            variant="unstyled"
            whiteSpace="nowrap"
            onClick={() =>
              trackEvent("book_club_join_click", {
                placement: "announcement",
              })
            }
          >
            <span className="book-club-promo-cta-content">
              JOIN THE BOOK CLUB
            </span>
          </Button>
        </Flex>
      </Container>
    </Box>
  );
};

const BookClubPromo = () => {
  const club = useBookClub();

  if (!club) {
    return null;
  }

  return (
    <Box
      as="section"
      id="book-club-promo"
      aria-labelledby="book-club-promo-title"
      bg="#0B0D19"
      borderTopWidth="1px"
      borderBottomWidth="1px"
      borderColor="whiteAlpha.100"
      py={24}
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        inset={0}
        bgImage='url("/images/book-club-energy.jpg")'
        bgPosition={{ base: "75% center", md: "center" }}
        bgSize="cover"
        opacity={{ base: 0.3, md: 0.4 }}
        pointerEvents="none"
        aria-hidden="true"
      />
      <Box
        position="absolute"
        inset={0}
        bgGradient="linear(to-b, rgba(11, 13, 25, 0.96), rgba(11, 13, 25, 0.74) 48%, rgba(11, 13, 25, 0.97))"
        pointerEvents="none"
        aria-hidden="true"
      />

      <Container maxW="container.xl" position="relative" zIndex={1}>
        <Box mb={{ base: 5, md: 6 }} textAlign="center">
          <Heading
            as="h2"
            id="book-club-promo-title"
            color="white"
            fontSize={{ base: "3xl", md: "4xl" }}
            lineHeight="1.05"
            letterSpacing="-0.03em"
          >
            Join the next Book Club
          </Heading>
          <Text mt={2} color="gray.400" fontSize={{ base: "md", md: "lg" }}>
            {formatBookClubSchedule(club.startsAt, club.endsAt)}
          </Text>
        </Box>

        <Box
          className="book-club-promo-card"
          overflow="hidden"
          bg="gray.700"
          borderWidth="1px"
          borderColor="whiteAlpha.200"
          borderRadius="2xl"
          boxShadow="0 18px 45px rgba(0, 0, 0, 0.24)"
        >
          <Flex
            position="relative"
            zIndex={1}
            direction={{ base: "column", md: "row" }}
          >
            <Box
              position="relative"
              flex={{ md: "0 0 35%" }}
              aspectRatio={{ base: "16 / 9", md: "auto" }}
              bg="gray.800"
            >
              {club.cover ? (
                <Image
                  src={club.cover}
                  alt={`Artwork for ${club.title}`}
                  position="absolute"
                  inset={0}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                />
              ) : (
                <Flex
                  position="absolute"
                  inset={0}
                  align="center"
                  justify="center"
                >
                  <Text fontSize="xs" letterSpacing="0.18em" color="red.200">
                    REDWING BOOK CLUB
                  </Text>
                </Flex>
              )}
            </Box>

            <Box p={{ base: 6, md: 8, lg: 10 }} flex="1" minW={0}>
              <Heading
                as="h3"
                color="white"
                fontSize={{ base: "xl", md: "2xl" }}
              >
                {club.title}
              </Heading>
              <Text
                mt={4}
                maxW="58ch"
                color="gray.300"
                fontSize="md"
                lineHeight="1.7"
              >
                {club.description}
              </Text>
            </Box>
          </Flex>
        </Box>

        <Flex
          mt={6}
          direction="column"
          align="center"
          gap={3}
          textAlign="center"
        >
          <Text
            maxW="52ch"
            color="gray.300"
            fontSize={{ base: "sm", md: "md" }}
          >
            The Redwing book club is free to join, no subscription required.
          </Text>
          <Link
            to={buildAuthRedirectPath("/login", BOOK_CLUB_PATH)}
            aria-label="Join the Book Club"
            className="button button-primary button-shadow button-wide-mobile book-club-promo-cta"
            onClick={() =>
              trackEvent("book_club_join_click", { placement: "promo" })
            }
          >
            <span className="book-club-promo-cta-content">
              JOIN THE BOOK CLUB
            </span>
          </Link>
        </Flex>
      </Container>
    </Box>
  );
};

export default BookClubPromo;
